import { locale } from "./index.js";

/* ================================================================
   Ländernamen.

   Das Land ist im Kellerbuch ein Freitextfeld — „Spanien" bleibt beim
   Sprachwechsel sonst „Spanien". Erkannte Namen werden über ihren
   ISO-Code in die Anzeigesprache übersetzt; alles Unbekannte bleibt
   unverändert stehen, statt geraten zu werden.

   Die Zuordnung Name → Code entsteht nicht als handgepflegte Liste,
   sondern beim ersten Zugriff aus den Ländernamen selbst: für jeden
   Code werden die Schreibweisen aller unterstützten Sprachen (und der
   gebräuchlichsten Importsprachen) eingesammelt. So wird auch ein
   Land erkannt, an das beim Schreiben dieser Zeilen niemand gedacht
   hat — eine kurze Liste hatte genau das jahrelang verpasst.
   ================================================================ */

/* ISO-3166-1-Alpha-2 aller Staaten, die als Herkunft in Frage kommen.
   Lieber großzügig: der Aufwand je Code ist ein Nachschlagen. */
const CODES = [
  "AD", "AE", "AF", "AG", "AL", "AM", "AO", "AR", "AT", "AU", "AZ",
  "BA", "BB", "BD", "BE", "BF", "BG", "BH", "BI", "BJ", "BN", "BO", "BR", "BS", "BT", "BW", "BY", "BZ",
  "CA", "CD", "CF", "CG", "CH", "CI", "CL", "CM", "CN", "CO", "CR", "CU", "CV", "CY", "CZ",
  "DE", "DJ", "DK", "DM", "DO", "DZ",
  "EC", "EE", "EG", "ER", "ES", "ET",
  "FI", "FJ", "FR",
  "GA", "GB", "GD", "GE", "GH", "GM", "GN", "GQ", "GR", "GT", "GW", "GY",
  "HN", "HR", "HT", "HU",
  "ID", "IE", "IL", "IN", "IQ", "IR", "IS", "IT",
  "JM", "JO", "JP",
  "KE", "KG", "KH", "KI", "KM", "KN", "KP", "KR", "KW", "KZ",
  "LA", "LB", "LC", "LI", "LK", "LR", "LS", "LT", "LU", "LV", "LY",
  "MA", "MC", "MD", "ME", "MG", "MH", "MK", "ML", "MM", "MN", "MR", "MT", "MU", "MV", "MW", "MX", "MY", "MZ",
  "NA", "NE", "NG", "NI", "NL", "NO", "NP", "NZ",
  "OM",
  "PA", "PE", "PG", "PH", "PK", "PL", "PS", "PT", "PY",
  "QA",
  "RO", "RS", "RU", "RW",
  "SA", "SB", "SC", "SD", "SE", "SG", "SI", "SK", "SL", "SM", "SN", "SO", "SR", "SS", "ST", "SV", "SY", "SZ",
  "TD", "TG", "TH", "TJ", "TL", "TM", "TN", "TR", "TT", "TW", "TZ",
  "UA", "UG", "US", "UY", "UZ",
  "VA", "VC", "VE", "VN", "VU",
  "WS",
  "XK",
  "YE",
  "ZA", "ZM", "ZW",
];

/* Sprachen, in denen ein Land geschrieben sein kann: die vier
   Oberflächensprachen plus Italienisch als häufige Importquelle. */
const SPRACHEN_FUER_NAMEN = ["de", "en", "es", "fr", "it"];

/* Gebräuchliche Sonderformen, die kein Ländername im engeren Sinn
   sind — Landesteile, Kurz- und Altformen, Tippgewohnheiten. */
const ALIAS = {
  "usa": "US", "u s a": "US", "vereinigte staaten von amerika": "US",
  "united states of america": "US", "amerika": "US", "america": "US",
  "england": "GB", "schottland": "GB", "scotland": "GB", "wales": "GB",
  "nordirland": "GB", "northern ireland": "GB", "grossbritannien": "GB",
  "great britain": "GB", "britain": "GB", "uk": "GB", "gb": "GB",
  "bundesrepublik deutschland": "DE", "brd": "DE",
  "holland": "NL", "die niederlande": "NL",
  "tschechische republik": "CZ", "tschechei": "CZ", "czechia": "CZ",
  "republik suedafrika": "ZA", "rsa": "ZA",
  "korea": "KR",
  "nord mazedonien": "MK", "mazedonien": "MK", "macedonia": "MK",
  "bosnien": "BA", "herzegowina": "BA",
  "ivory coast": "CI",
  "kap verde": "CV", "burma": "MM",
  "vatikan": "VA", "heiliger stuhl": "VA",
  "kosovo": "XK",
  "moldawien": "MD", "moldau": "MD", "moldova": "MD",
  "weissrussland": "BY", "belorussland": "BY",
  "swasiland": "SZ", "osttimor": "TL", "ost timor": "TL",
  "kirgistan": "KG", "kirgisien": "KG", "birma": "MM",
  "kapverden": "CV", "kap verden": "CV",
  "elfenbeinkuste": "CI", "sued korea": "KR", "nord korea": "KP",
  "neu seeland": "NZ", "sued afrika": "ZA", "vae": "AE",
  "bosnien herzegowina": "BA",
  "schweizerische eidgenossenschaft": "CH",
  "oesterreich ungarn": "AT",
};

/* Vergleichsformen: ohne Groß-/Kleinschreibung, ohne Satzzeichen.
   Umlaute werden auf zwei Arten aufgelöst, weil beide vorkommen —
   „Osterreich" (Akzent weggelassen) und „Oesterreich" (umschrieben).
   Jeder Name wird unter beiden Formen abgelegt und danach gesucht. */
const grundform = (name) => String(name || "").trim().toLowerCase().replace(/ß/g, "ss");
const feilen = (s) => s.replace(/[^a-z0-9]+/g, " ").trim();

function normiert(name) {
  return feilen(grundform(name).normalize("NFD").replace(/[\u0300-\u036f]/g, ""));
}
function normiertUmschrift(name) {
  return feilen(grundform(name)
    .replace(/ä/g, "ae").replace(/ö/g, "oe").replace(/ü/g, "ue")
    .normalize("NFD").replace(/[\u0300-\u036f]/g, ""));
}

let verzeichnis = null;
function aufbauen() {
  const m = new Map();
  const setz = (name, code) => {
    for (const k of [normiert(name), normiertUmschrift(name)]) {
      if (k && !m.has(k)) m.set(k, code);
    }
  };
  for (const code of CODES) {
    setz(code, code);
    for (const sprache of SPRACHEN_FUER_NAMEN) {
      try {
        const anzeige = new Intl.DisplayNames([sprache], { type: "region" }).of(code);
        if (anzeige && anzeige !== code) setz(anzeige, code);
      } catch (e) { /* Sprache nicht verfügbar — die übrigen genügen */ }
    }
  }
  /* Sonderformen zuletzt, aber ohne bestehende Namen zu verdrängen. */
  for (const [name, code] of Object.entries(ALIAS)) setz(name, code);
  return m;
}

function isoVon(name) {
  const k = normiert(name);
  if (!k) return null;
  if (!verzeichnis) verzeichnis = aufbauen();
  return verzeichnis.get(k) || verzeichnis.get(normiertUmschrift(name)) || null;
}

/* Anzeigename in der aktuellen Sprache; unbekannte Namen unverändert. */
function landName(name) {
  const iso = isoVon(name);
  if (!iso) return name;
  try {
    const anzeige = new Intl.DisplayNames([locale()], { type: "region" });
    return anzeige.of(iso) || name;
  } catch (e) {
    return name;
  }
}

export { isoVon, landName };
